import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type {
  AiCostGranularity,
  IAiCostBucket,
  IAiCostBreakdown,
  IAiCostSummary,
} from '@skystone/shared';
import { aiApiLogApi } from '@/services/ai-api-log-api';
import { formatCostUsd } from '@/lib/format-cost';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorBanner,
  PageHeader,
  PageLoading,
  Spinner,
} from '@/components/ui';
import { PageContainer } from '@/components/layout';

interface WindowConfig {
  granularity: AiCostGranularity;
  defaultSize: number;
  min: number;
  max: number;
  label: string;
  unit: string;
}

const WINDOW_CONFIGS: WindowConfig[] = [
  { granularity: 'day', defaultSize: 7, min: 1, max: 90, label: 'Daily cost', unit: 'days' },
  { granularity: 'week', defaultSize: 4, min: 1, max: 26, label: 'Weekly cost', unit: 'weeks' },
  { granularity: 'month', defaultSize: 3, min: 1, max: 12, label: 'Monthly cost', unit: 'months' },
];

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.max(min, Math.min(max, Math.trunc(value)));
}

function formatBucketLabel(bucketStart: string, granularity: AiCostGranularity): string {
  const date = new Date(`${bucketStart}T00:00:00Z`);
  if (granularity === 'day') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  if (granularity === 'week') {
    return `Wk ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

interface CostChartProps {
  config: WindowConfig;
}

function CostChart({ config }: Readonly<CostChartProps>) {
  const [windowSize, setWindowSize] = useState(config.defaultSize);
  const [data, setData] = useState<IAiCostSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debounce so dragging the up/down arrow doesn't fire one request per step.
  useEffect(() => {
    const handle = setTimeout(() => {
      setLoading(true);
      setError(null);
      aiApiLogApi
        .getCostSummary(config.granularity, windowSize)
        .then((s) => setData(s))
        .catch((err: unknown) =>
          setError(err instanceof Error ? err.message : 'Failed to load cost data.'),
        )
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [config.granularity, windowSize]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.buckets.map((b: IAiCostBucket) => ({
      label: formatBucketLabel(b.bucketStart, config.granularity),
      costUsd: Number(b.costUsd.toFixed(6)),
      callCount: b.callCount,
    }));
  }, [data, config.granularity]);

  const total = data?.total ?? 0;
  const totalText = formatCostUsd(total) ?? '$0.0000';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>{config.label}</CardTitle>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Last</span>
            <input
              type="number"
              min={config.min}
              max={config.max}
              step={1}
              value={windowSize}
              onChange={(e) => setWindowSize(clamp(Number(e.target.value), config.min, config.max))}
              aria-label={`${config.label} window size in ${config.unit}`}
              className="w-20 rounded-md border border-input bg-background px-2 py-1 text-right focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <span className="text-muted-foreground">{config.unit}</span>
          </label>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <ErrorBanner message={error} />}
        {loading && !data ? (
          <div className="flex h-[260px] items-center justify-center"><Spinner size="md" /></div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 24, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                className="fill-muted-foreground"
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                className="fill-muted-foreground"
                tickFormatter={(v: number) => formatCostUsd(v) ?? '$0.0000'}
                width={80}
              />
              <Tooltip
                formatter={(value, _name, item) => {
                  const numericValue = typeof value === 'number' ? value : 0;
                  const payload = (item as { payload?: { callCount?: number } }).payload;
                  return [
                    `${formatCostUsd(numericValue) ?? '$0.0000'} (${payload?.callCount ?? 0} calls)`,
                    'Cost',
                  ];
                }}
              />
              <Bar dataKey="costUsd" fill="var(--color-chart-1)" />
            </BarChart>
          </ResponsiveContainer>
        )}
        <p className="text-sm text-muted-foreground sm:text-right">
          Total in window: <span className="font-medium text-foreground">{totalText}</span>
        </p>
      </CardContent>
    </Card>
  );
}

interface SummaryCardProps {
  label: string;
  value: number;
}

function SummaryCard({ label, value }: Readonly<SummaryCardProps>) {
  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums md:text-3xl">
          {formatCostUsd(value) ?? '$0.0000'}
        </p>
      </CardContent>
    </Card>
  );
}

interface BreakdownSectionProps {
  data: IAiCostBreakdown | null;
}

function BreakdownSection({ data }: Readonly<BreakdownSectionProps>) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Breakdown</CardTitle>
        <p className="text-xs text-muted-foreground">Last 30 days</p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <BreakdownColumn title="By endpoint" rows={data?.byEndpoint ?? []} />
        <BreakdownColumn title="By model" rows={data?.byModel ?? []} />
        <BreakdownColumn title="Top users" rows={data?.byUser ?? []} />
      </CardContent>
    </Card>
  );
}

interface BreakdownColumnProps {
  title: string;
  rows: { key: string; label: string; costUsd: number }[];
}

function BreakdownColumn({ title, rows }: Readonly<BreakdownColumnProps>) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activity in this period.</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.key} className="flex items-center justify-between text-sm">
              <span className="truncate" title={r.label}>{r.label}</span>
              <span className="tabular-nums text-muted-foreground">
                {formatCostUsd(r.costUsd) ?? '$0.0000'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface SummaryTotals {
  today: number;
  week: number;
  month: number;
  total: number;
}

export function AdminAiCostPage() {
  const [summaryTotals, setSummaryTotals] = useState<SummaryTotals | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<IAiCostBreakdown | null>(null);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    setSummaryError(null);
    try {
      // Today / week / month / all-time. We treat "all-time" as a 12-month
      // rolling window — sufficient since cost tracking only started days ago
      // and the column doesn't predate this epic.
      const [day1, week1, month1, year1] = await Promise.all([
        aiApiLogApi.getCostSummary('day', 1),
        aiApiLogApi.getCostSummary('week', 1),
        aiApiLogApi.getCostSummary('month', 1),
        aiApiLogApi.getCostSummary('month', 12),
      ]);
      setSummaryTotals({
        today: day1.total,
        week: week1.total,
        month: month1.total,
        total: year1.total,
      });
    } catch (err: unknown) {
      setSummaryError(err instanceof Error ? err.message : 'Failed to load cost summary.');
    }
  }, []);

  const loadBreakdown = useCallback(async () => {
    setBreakdownError(null);
    try {
      const data = await aiApiLogApi.getCostBreakdown();
      setBreakdown(data);
    } catch (err: unknown) {
      setBreakdownError(err instanceof Error ? err.message : 'Failed to load breakdown.');
    }
  }, []);

  useEffect(() => {
    void Promise.all([loadSummary(), loadBreakdown()]).finally(() => setInitialLoading(false));
  }, [loadSummary, loadBreakdown]);

  if (initialLoading) {
    return <PageLoading />;
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="AI Cost"
        subtitle="OpenAI spend on Insight chat surfaces. Cost tracking starts at launch — earlier calls show as zero."
      />

      <ErrorBanner message={summaryError} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard label="Today" value={summaryTotals?.today ?? 0} />
        <SummaryCard label="This week" value={summaryTotals?.week ?? 0} />
        <SummaryCard label="This month" value={summaryTotals?.month ?? 0} />
        <SummaryCard label="Total (12 mo)" value={summaryTotals?.total ?? 0} />
      </div>

      {WINDOW_CONFIGS.map((config) => (
        <CostChart key={config.granularity} config={config} />
      ))}

      <ErrorBanner message={breakdownError} />
      <BreakdownSection data={breakdown} />
    </PageContainer>
  );
}
